import { useCallback, useEffect, useRef } from 'react';

import { DEFAULT_PAYMENT_METHODS } from '../../../config/constants';
import { analytics } from '../../../services/analytics';
import { intercom } from '../../../services/intercom';
import { useFlag } from '../../../services/launchDarkly';
import { isBingoCardsPackage } from '../../../stores/bingo/helpers';
import { EnumOpenDialog } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';
import { SKRILL_SESSION_ID_STORAGE_KEY } from '../../payments/constants';
import { assumeForeignHtmlStyle, isPaymentsAppMessage } from '../shared/helpers';
import { isSubscriptionPackage } from '../v2/types.utils';

export function useMessagesListener(
  setIframeStyle: (style: { height?: number | string }) => void,
  setIframeLoading: (loading: boolean) => void,
  authToken?: string,
  isMiniStore?: boolean,
) {
  const { bingoStore, uiStore, payRedeemStore, portalStore, appStore, eventTriggerStore } = useStores();

  const paymentMethods = useFlag('payment_methods', DEFAULT_PAYMENT_METHODS);

  const isForeign = useRef(false);

  const onMessage = useCallback(
    (event: MessageEvent<unknown>) => {
      if (!isPaymentsAppMessage(event.data)) {
        return;
      }

      if ('action' in event.data) {
        if (event.data.action === 'close') {
          portalStore.closePortal();
          payRedeemStore.awaitForTransaction();
        }

        return;
      }

      if (!('class' in event.data)) {
        isForeign.current = true;
        return;
      }

      switch (event.data.class) {
        case 'analytics':
          if (event.data.type === 'event' && event.data.details) {
            void analytics.sendEvent(event.data.details.name, event.data.details.params);
          }
          isForeign.current = false;
          return;

        case 'flow': {
          switch (event.data.type) {
            case 'size-change':
              if (!isForeign.current) {
                setIframeStyle({ height: event.data.details?.height });
              }
              return;
            case 'loading-finished':
              setIframeLoading(false);
              return;
            case 'back-to-lobby': {
              const onClose = portalStore.options.onClose;

              portalStore.closePortal();

              if (onClose && typeof onClose === 'function') {
                onClose();
                return;
              }

              uiStore.setCloseDialog();
              return;
            }
            case 'exit': {
              const onClose = portalStore.options.onClose;

              const { eventTriggerData } = appStore.currentOrderPackage || {};

              if (onClose && typeof onClose === 'function') {
                portalStore.closePortal();
                onClose();
                return;
              }

              if (uiStore.openDialog?.options?.onClose && typeof uiStore.openDialog.options.onClose === 'function') {
                uiStore.openDialog.options.onClose?.();
              } else if (eventTriggerData?.activeEvent) {
                portalStore.closePortal();
                uiStore.setCloseDialog();
                void eventTriggerStore.openPopup(eventTriggerData?.activeEvent);
                return;
              }

              const closeDialogAnd = (next?: () => void) => {
                portalStore.closePortal();
                uiStore.setCloseDialog();
                if (next) next();
              };

              if (isMiniStore) {
                closeDialogAnd(() => uiStore.setIsVisibleMiniStore(true));
                return;
              }

              if (
                isSubscriptionPackage(appStore.selectedPackage) ||
                (appStore.selectedPackage && isBingoCardsPackage(appStore.selectedPackage))
              ) {
                closeDialogAnd(() => {
                  bingoStore.openBingoStore();
                });
                return;
              }

              closeDialogAnd(() => uiStore.setOpenFullPageDialog('store'));
              return;
            }
            case 'success': {
              const details = event.data.details || {
                amount: appStore.selectedPackage?.price,
                transactionId: '',
                isFirstDeposit: false,
              };
              const { amount, transactionId, isFirstDeposit, paymentMethod, actualGrantedRewards } = details;
              if (transactionId) {
                payRedeemStore.setInProcessTransactionId(transactionId);
                payRedeemStore.transactionsEventsRegistry.set(transactionId, {
                  amount: amount ?? appStore.selectedPackage?.price ?? 0,
                  isFirstDeposit,
                  transactionId,
                  paymentMethod,
                  selectedPackage: appStore.selectedPackage ?? undefined,
                  actualGrantedRewards,
                });
              }
              portalStore.closePortal();
              payRedeemStore.handleSuccessPurchase();
              appStore.removeNpuStore();
              return;
            }
            case 'redirect': {
              isForeign.current = true;
              const styles = assumeForeignHtmlStyle(event.data.details?.url);
              setIframeStyle(styles);
              return;
            }
            case 'buttonAction':
              if (event.data.details?.kind === 'support') {
                intercom.handleOpenSupport();
              }
              return;
            case 'authenticate':
              event.source?.postMessage(
                {
                  type: 'authenticate',
                  details: {
                    token: authToken,
                    paymentMethods,
                  },
                },
                { targetOrigin: event.origin },
              );
              return;
            case 'skrillSessionStarted':
              if (event.data.details) {
                sessionStorage.setItem(SKRILL_SESSION_ID_STORAGE_KEY, event.data.details.sessionId);
              }
          }
        }
      }
    },
    [
      portalStore,
      payRedeemStore,
      setIframeLoading,
      bingoStore,
      uiStore,
      setIframeStyle,
      authToken,
      paymentMethods,
      appStore,
      isMiniStore,
      eventTriggerStore,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', onMessage);
    return () => {
      uiStore.unblockDialog(EnumOpenDialog.isOpenTransactionFailedDialog);
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage, uiStore]);
}
